import { useId, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import { AppIcon } from "~/components/ui/dynamic-icon";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { ICON_OPTIONS } from "~/pages/manifold-nextgen/components/quick-actions/constants";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentStarterPrompt,
  AgentStarterPromptInput,
  MAX_STARTER_PROMPTS,
  STARTER_PROMPT_MAX_LENGTH,
  STARTER_PROMPT_TITLE_MAX_LENGTH,
} from "~/services/agents";
import {
  AgentSortableListTab,
  EditFormFooter,
  type EditFormRenderProps,
} from "./AgentSortableListTab";

const toInput = (item: AgentStarterPrompt): AgentStarterPromptInput => ({
  text: item.text,
  title: item.title,
  icon: item.icon,
});

const StarterPromptView = ({ item }: { item: AgentStarterPrompt }) => (
  <div className="flex min-w-0 flex-1 items-center gap-2">
    {item.icon && (
      <AppIcon name={item.icon} fallback={Sparkles} className="size-4 shrink-0 text-foreground" />
    )}
    <div className="min-w-0">
      {item.title && <p className="truncate text-sm font-semibold text-foreground">{item.title}</p>}
      <p className={cn("text-sm", item.title ? "text-muted-foreground" : "text-foreground")}>
        {item.text}
      </p>
    </div>
  </div>
);

const StarterPromptIconPicker = ({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (icon: string | null) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label="Choose icon"
          className="flex size-14 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-muted transition-colors hover:bg-muted/70 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {value ? (
            <AppIcon name={value} fallback={Sparkles} className="size-4 text-foreground" />
          ) : (
            <span className="text-xs text-muted-foreground">None</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[1300] w-72 p-3">
        <div className="grid grid-cols-6 gap-2">
          <button
            type="button"
            aria-label="No icon"
            aria-pressed={value === null}
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className={cn(
              "flex aspect-square items-center justify-center rounded-md border text-[10px] text-muted-foreground transition-colors",
              value === null ? "border-primary bg-primary/10" : "border-transparent hover:bg-muted"
            )}
          >
            None
          </button>
          {ICON_OPTIONS.map((name) => (
            <button
              key={name}
              type="button"
              aria-label={name}
              aria-pressed={name === value}
              onClick={() => {
                onChange(name);
                setOpen(false);
              }}
              className={cn(
                "flex aspect-square items-center justify-center rounded-md border transition-colors",
                name === value
                  ? "border-primary bg-primary/10"
                  : "border-transparent hover:bg-muted"
              )}
            >
              <DynamicIcon name={name} className="size-4 text-foreground" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const StarterPromptEditForm = ({
  initial,
  disabled,
  onSave,
  onCancel,
}: EditFormRenderProps<AgentStarterPromptInput>) => {
  const titleId = useId();
  const textId = useId();
  const [title, setTitle] = useState(initial.title ?? "");
  const [icon, setIcon] = useState<string | null>(initial.icon ?? null);
  const [text, setText] = useState(initial.text);

  const trimmedText = text.trim();
  const trimmedTitle = title.trim();
  const canSave =
    trimmedText.length > 0 &&
    trimmedText.length <= STARTER_PROMPT_MAX_LENGTH &&
    trimmedTitle.length <= STARTER_PROMPT_TITLE_MAX_LENGTH &&
    !disabled;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({ text: trimmedText, title: trimmedTitle || null, icon });
  };

  return (
    <>
      <div className="flex gap-4">
        <div className="space-y-1.5">
          <Label>Icon</Label>
          <StarterPromptIconPicker value={icon} onChange={setIcon} disabled={disabled} />
        </div>
        <div className="flex-1 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={titleId}>Title</Label>
            <Input
              id={titleId}
              autoFocus
              placeholder="e.g. Differential expression"
              maxLength={STARTER_PROMPT_TITLE_MAX_LENGTH}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={textId} className="gap-0">
              Prompt <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id={textId}
              rows={3}
              placeholder="e.g. Build a Kaplan-Meier curve for overall survival, stratified by treatment arm…"
              maxLength={STARTER_PROMPT_MAX_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={disabled}
            />
            <span className="text-xs text-muted-foreground">
              {text.length} / {STARTER_PROMPT_MAX_LENGTH}
            </span>
          </div>
        </div>
      </div>
      <EditFormFooter
        canSave={canSave}
        disabled={disabled}
        onCancel={onCancel}
        onSave={handleSubmit}
      />
    </>
  );
};

export const AgentStarterPromptsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);

  return (
    <AgentSortableListTab<AgentStarterPrompt, AgentStarterPromptInput>
      title="Starter Prompts"
      context="Shown at the start of new conversations with this agent"
      description={`Write up to ${MAX_STARTER_PROMPTS} starter prompts that demonstrate what users can ask of this agent. Drag to reorder — the first prompt appears in the top-left of the conversation start screen.`}
      agent={agent}
      field="starter_prompts"
      items={agent.starter_prompts}
      maxItems={MAX_STARTER_PROMPTS}
      canEdit={canEdit}
      toInput={toInput}
      emptyDraft={{ text: "", title: null, icon: null }}
      renderView={(item) => <StarterPromptView item={item} />}
      renderEditForm={(props) => <StarterPromptEditForm {...props} />}
      addLabel="Add prompt"
      emptyMessage="No starter prompts yet."
      errorMessage="Failed to save starter prompts"
    />
  );
};
